import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import CTASection from '@/components/feature/CTASection';
import SEOHead from '@/components/base/SEOHead';

const SITE_URL = (typeof import.meta !== 'undefined' && (import.meta as Record<string, unknown>).env?.VITE_SITE_URL as string) || '';

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE_URL}/services#webpage`,
  name: '服務項目 | 得運智科股份有限公司',
  url: `${SITE_URL}/services`,
  description: '從資源對接、財稅管理、AI 應用到 ESG 永續，得運智科協助企業建立更完整的升級轉型路徑。四大核心服務：產官學資源整合、國考級財稅管理顧問、AI 提示工程與數位轉型、ESG 碳管理與稽核。',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首頁', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: '服務項目', item: `${SITE_URL}/services` },
    ],
  },
  mainEntity: {
    '@type': 'ItemList',
    itemListElement: [
      {
        '@type': 'Service',
        position: 1,
        name: '產官學資源整合與育成',
        description: '協助企業盤點現況、尋找合適的政府輔導資源、產學合作機會與外部支持方案。',
        provider: { '@type': 'Organization', name: '得運智科股份有限公司' },
        areaServed: { '@type': 'Country', name: 'TW' },
      },
      {
        '@type': 'Service',
        position: 2,
        name: '國考級財稅與管理顧問',
        description: '以專業財稅顧問角度，協助企業檢視財務結構、稅務合規與營運管理問題。',
        provider: { '@type': 'Organization', name: '得運智科股份有限公司' },
        areaServed: { '@type': 'Country', name: 'TW' },
      },
      {
        '@type': 'Service',
        position: 3,
        name: 'AI 提示工程與數位轉型',
        description: '協助企業導入生成式 AI、建立內部知識庫、設計 AI SOP，讓 AI 能真正進入日常工作流程。',
        provider: { '@type': 'Organization', name: '得運智科股份有限公司' },
        areaServed: { '@type': 'Country', name: 'TW' },
      },
      {
        '@type': 'Service',
        position: 4,
        name: 'ESG 碳管理與稽核服務',
        description: '從碳盤查、碳足跡、永續報告到供應鏈合規，逐步建立永續競爭力。',
        provider: { '@type': 'Organization', name: '得運智科股份有限公司' },
        areaServed: { '@type': 'Country', name: 'TW' },
      },
    ],
  },
};

const services = [
  {
    id: 'gov',
    title: '產官學資源整合與育成',
    description: '企業成長需要的不只是努力，更需要正確資源與適合的合作夥伴。得運智科協助企業盤點現況、尋找合適的政府輔導資源、產學合作機會與外部支持方案，讓企業能更有效率地取得成長動能。',
    image: 'https://public.readdy.ai/ai/img_res/edited_f2838a634804c8ffc2e46f16e07ec4c6_628fc175.jpg'
    ,
    items: [
      '政府計畫對接與申請輔導',
      'SBIR、CITD、SIIR 等計畫方向評估',
      '產學合作與大專院校研發能量媒合',
      '商標佈局與智慧財產權策略建議',
      '青年創業貸款與中小企業信保貸款計畫書輔導',
      '創業育成與營運資源整合',
    ],
  },
  {
    id: 'tax',
    title: '國考級財稅與管理顧問',
    description: '穩健的財稅制度，是企業能否長期發展的重要基礎。得運智科以專業財稅顧問角度，協助企業檢視財務結構、稅務合規與營運管理問題，讓企業在成長過程中降低風險、提升決策品質。',
    image: 'https://public.readdy.ai/ai/img_res/edited_86fbf305e2722d3809e008ce0c0db01f_58f65f60.jpg'
    ,
    items: [
      '年度所得稅與稅務規劃建議',
      '企業財務健檢與報表分析',
      '營運現金流與成本結構優化',
      '公司設立與股權結構規劃',
      '勞資法規遵循與人事流程建議',
      '勞資爭議與管理流程協處',
    ],
  },
  {
    id: 'ai',
    title: 'AI 提示工程與數位轉型',
    description: 'AI 不只是工具，更是企業提升效率與重塑流程的機會。得運智科協助企業導入生成式 AI、建立內部知識庫、設計 AI SOP，讓 AI 能真正進入日常工作流程，而不是停留在試用階段。',
    image: 'https://public.readdy.ai/ai/img_res/edited_821f7459b6ee229df0b66474489622e0_98a9dcda.jpg'
    ,
    items: [
      'ISO 17024 AI 認證導向提示工程培訓',
      '企業 AI SOP 建立',
      '行政、行銷、採購與文件流程 AI 化',
      '生成式 AI 文件撰寫與資料整理',
      '市場分析與營運預測輔助',
      '專屬知識庫與企業內部 AI 助理規劃',
    ],
  },
  {
    id: 'esg',
    title: 'ESG 碳管理與稽核服務',
    description: '面對供應鏈減碳、國際永續規範與 ESG 要求，企業需要及早建立可追蹤、可管理、可溝通的永續資料基礎。得運智科協助企業從碳盤查、碳足跡、永續報告到供應鏈合規，逐步建立永續競爭力。',
    image: 'https://public.readdy.ai/ai/img_res/edited_acf53478df090945b28430ed3d3b462a_f9db2db2.jpg'
    ,
    items: [
      'ISO 14064-1 組織溫室氣體盤查輔導',
      'ISO 14067 產品碳足跡核算輔導',
      '碳熱點識別與減碳目標規劃',
      '碳資產管理策略建議',
      '永續報告書與 ESG 資料整理',
      '供應鏈 ESG 稽核與合規準備',
    ],
  },
];

export default function ServicesPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <SEOHead
        title="服務項目 | 得運智科股份有限公司"
        description="從資源對接、財稅管理、AI 應用到 ESG 永續，得運智科協助企業建立更完整的升級轉型路徑。產官學資源整合、國考級財稅管理顧問、AI 提示工程與數位轉型、ESG 碳管理與稽核服務。"
        keywords="企業顧問服務, 產官學資源, 財稅管理, AI 數位轉型, ESG 永續, 碳管理, 政府資源申請"
        canonicalPath="/services"
        ogType="website"
        jsonLd={jsonLd}
      />
      <Navbar />
      <main>
        {/* Hero with background image */}
        <section className="relative min-h-[400px] md:min-h-[500px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/edited_75efb04f8a485311cf8d87c5ca7f5825_f9ae3877.jpg"
              alt="服務項目背景"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-primary-900/35 via-primary-900/20 to-primary-900/45" />
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 lg:px-8 text-center pt-16">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight">
              服務項目
            </h1>
            <p className="text-sm md:text-base text-white/90 max-w-2xl mx-auto leading-relaxed">
              從資源對接、財稅管理、AI 應用到 ESG 永續，得運智科協助企業建立更完整的升級轉型路徑。
            </p>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-16 md:py-24">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service) => (
                <div
                  key={service.id}
                  className="bg-background-50 rounded-xl overflow-hidden border border-background-200 hover:border-primary-300 transition-colors duration-300"
                >
                  <div className="h-52 md:h-60 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6 md:p-8">
                    <h3 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3 leading-tight">
                      {service.title}
                    </h3>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-6">
                      {service.description}
                    </p>
                    <ul className="space-y-2.5 mb-6">
                      {service.items.map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-check-line text-accent-500 text-sm" />
                          </div>
                          <span className="text-sm text-foreground-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                    <a
                      href="/contact"
                      className="inline-block bg-primary-600 text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-primary-700 transition-colors duration-200 whitespace-nowrap shadow-md shadow-primary-600/15"
                    >
                      預約諮詢
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </div>
  );
}